/*
 * SiteHeader — After-Hours Editorial
 * Ink-black bar, gold hairline, spaced small-caps wordmark with script "Porsha".
 */
import { Link, useLocation } from "wouter";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { sections } from "@/content/sections";
import ResponsiveImage from "@/components/ResponsiveImage";

const navSections = [...sections].sort((a, b) => a.navOrder - b.navOrder);

export function Wordmark({ compact = false }: { compact?: boolean }) {
  return (
    <Link href="/" className="group inline-flex shrink-0 items-center gap-3">
      <ResponsiveImage
        src="/manus-storage/logo-mark_a78a62da.png"
        alt="According to Porsha monogram"
        priority
        sizes="40px"
        className={compact ? "h-9 w-9" : "h-10 w-10"}
      />
      <span className="leading-none w-[7.5rem]">
        <span
          className="block text-[0.58rem] font-bold uppercase"
          style={{ letterSpacing: "0.3em", color: "var(--gold)" }}
        >
          According to
        </span>
        <span
          className="font-script block text-[1.9rem]"
          style={{ color: "var(--violet)", lineHeight: 1.05 }}
        >
          Porsha
        </span>
      </span>
    </Link>
  );
}

export default function SiteHeader() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/92 backdrop-blur-md">
      <div className="container">
        <div className="flex items-center justify-between py-4">
          <Wordmark />
          <nav className="hidden lg:flex items-center gap-4 xl:gap-5">
            {navSections.map((s) => (
              <Link
                key={s.slug}
                href={`/${s.slug}`}
                data-active={location.startsWith(`/${s.slug}`)}
                className="nav-link whitespace-nowrap text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-foreground/80 hover:text-foreground transition-colors"
              >
                {s.slug === "the-tea" ? "Tea" : s.name}
              </Link>
            ))}
            <Link
              href="/subscribe"
              className="ml-2 border border-[var(--gold)] px-5 py-2.5 text-[0.72rem] font-bold uppercase tracking-[0.2em] text-[var(--gold)] transition-all duration-200 hover:bg-[var(--gold)]/10 hover:text-[var(--gold-2)] active:scale-[0.97]"
            >
              Subscribe
            </Link>
          </nav>
          <button
            className="lg:hidden text-foreground p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {/* Mobile menu */}
      {open && (
        <nav className="lg:hidden border-t border-border bg-background">
          <div className="container py-4 flex flex-col gap-1">
            {navSections.map((s) => (
              <Link
                key={s.slug}
                href={`/${s.slug}`}
                onClick={() => setOpen(false)}
                className="py-3 text-sm font-semibold uppercase tracking-[0.18em] text-foreground/85 border-b border-border/50"
              >
                {s.name}
              </Link>
            ))}
            <Link
              href="/subscribe"
              onClick={() => setOpen(false)}
              className="mt-3 border border-[var(--gold)] px-4 py-3 text-center text-sm font-bold uppercase tracking-[0.2em] text-[var(--gold)]"
            >
              Subscribe — The Monthly Edit
            </Link>
            <div className="mt-3 flex gap-6 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <Link href="/about" onClick={() => setOpen(false)}>About</Link>
              <Link href="/advertise" onClick={() => setOpen(false)}>Advertise</Link>
              <Link href="/contact" onClick={() => setOpen(false)}>Contact</Link>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
